/**
 * Customer app routing — translation between the legacy `Screen`
 * strings (used by every screen's `onNavigate` callback) and real
 * URLs.
 *
 * The customer app's screens were originally driven by an in-memory
 * `currentScreen` enum, with every screen receiving an
 * `onNavigate(screen, params)` callback. Slice P1 #1 swaps the
 * underlying engine for React Router without rewriting the call
 * sites — `onNavigate` remains the screen-facing API, backed by
 * `useNavigate()` from react-router via the helpers below.
 *
 * Why keep the Screen enum at all? Two reasons:
 *   1. Screens use `onNavigate('foo', { id })` from many call
 *      sites — rewriting them all in this slice would balloon the
 *      diff.
 *   2. The `Navigation` bottom-bar component matches a screen string
 *      against a `match` array to decide which tab to highlight.
 *      Keeping the enum means that lookup stays unchanged.
 */

export type Screen =
  | 'splash'
  | 'onboarding'
  | 'login'
  | 'identity-verification'
  | 'home'
  | 'product'
  | 'booking'
  | 'checkout'
  | 'courts'
  | 'court'
  | 'court-checkout'
  | 'shop'
  | 'shop-collection'
  | 'shop-product'
  | 'shop-cart'
  | 'clubs'
  | 'club'
  | 'favorites'
  | 'coaches'
  | 'coach'
  | 'book-coach'
  | 'coach-checkout'
  | 'package-checkout'
  | 'orders'
  | 'profile'
  | 'personal-info'
  | 'saved-addresses'
  | 'help-support'
  | 'notifications'
  | 'admin-verifications';

export type NavParams = {
  id?: string;
  venueId?: string | null;
  handle?: string;
  collectionHandle?: string;
  // Free-form for booking payloads etc — survives via location.state
  state?: Record<string, unknown>;
};

/** Convert a (screen, params) tuple into a URL path. */
export function screenToPath(screen: Screen, params?: NavParams): string {
  switch (screen) {
    case 'splash':                return '/';
    case 'onboarding':            return '/welcome';
    case 'login':                 return '/login';
    case 'identity-verification': return '/identity-verification';
    case 'home':                  return '/';
    // Legacy machine-rental flow.
    case 'product':               return '/rent';
    case 'booking':               return '/rent/booking';
    case 'checkout':              return '/rent/checkout';
    // Court flow.
    case 'courts':                return '/courts';
    case 'court':                 return params?.id ? `/courts/${params.id}` : '/courts';
    case 'court-checkout':        return '/courts/checkout';
    // Coach flow.
    case 'coaches':               return '/coaches';
    case 'coach':                 return params?.id ? `/coaches/${params.id}` : '/coaches';
    case 'book-coach':            return params?.id ? `/coaches/${params.id}/book` : '/coaches';
    case 'coach-checkout':        return '/coaches/checkout';
    case 'package-checkout':      return '/coaches/package-checkout';
    // Clubs (marketplace browse).
    case 'clubs':                 return '/clubs';
    case 'club':
      if (params?.id && params?.venueId) return `/clubs/${params.id}/venues/${params.venueId}`;
      if (params?.id)                    return `/clubs/${params.id}`;
      return '/clubs';
    // Shop (Shopify).
    case 'shop':                  return '/shop';
    case 'shop-collection':       return params?.collectionHandle ? `/shop/c/${params.collectionHandle}` : '/shop';
    case 'shop-product':          return params?.handle ? `/shop/p/${params.handle}` : '/shop';
    case 'shop-cart':             return '/shop/cart';
    // Activity.
    case 'orders':                return '/bookings';
    case 'favorites':             return '/profile/favorites';
    // Profile.
    case 'profile':               return '/profile';
    case 'personal-info':         return '/profile/personal';
    case 'saved-addresses':       return '/profile/addresses';
    case 'help-support':          return '/profile/help';
    case 'notifications':         return '/profile/notifications';
    // Admin (lives inside the customer app for now).
    case 'admin-verifications':   return '/admin/verifications';
  }
}

/**
 * Convert the current URL pathname back into a Screen value, used by
 * the Navigation bottom-bar component to decide which tab is lit.
 */
export function pathToScreen(pathname: string): Screen {
  // Order matters — match the most specific paths first.
  if (pathname === '/welcome') return 'onboarding';
  if (pathname === '/login') return 'login';
  if (pathname === '/identity-verification') return 'identity-verification';

  if (pathname.startsWith('/rent/booking')) return 'booking';
  if (pathname.startsWith('/rent/checkout')) return 'checkout';
  if (pathname.startsWith('/rent')) return 'product';

  if (pathname.startsWith('/courts/checkout')) return 'court-checkout';
  if (/^\/courts\/[^/]+/.test(pathname)) return 'court';
  if (pathname === '/courts') return 'courts';

  if (pathname.startsWith('/coaches/checkout')) return 'coach-checkout';
  if (pathname.startsWith('/coaches/package-checkout')) return 'package-checkout';
  if (/^\/coaches\/[^/]+\/book$/.test(pathname)) return 'book-coach';
  if (/^\/coaches\/[^/]+/.test(pathname)) return 'coach';
  if (pathname === '/coaches') return 'coaches';

  if (/^\/clubs\/[^/]+/.test(pathname)) return 'club';
  if (pathname === '/clubs') return 'clubs';

  if (pathname === '/shop/cart') return 'shop-cart';
  if (pathname.startsWith('/shop/c/')) return 'shop-collection';
  if (pathname.startsWith('/shop/p/')) return 'shop-product';
  if (pathname === '/shop' || pathname.startsWith('/shop/')) return 'shop';

  if (pathname === '/bookings') return 'orders';

  if (pathname === '/profile/favorites') return 'favorites';
  if (pathname === '/profile/personal') return 'personal-info';
  if (pathname === '/profile/addresses') return 'saved-addresses';
  if (pathname === '/profile/help') return 'help-support';
  if (pathname === '/profile/notifications') return 'notifications';
  if (pathname === '/profile' || pathname.startsWith('/profile')) return 'profile';

  if (pathname.startsWith('/admin/verifications')) return 'admin-verifications';

  return 'home';
}
